/**
 * WholesaleThresholds Component
 *
 * US-014: Wholesale Threshold Management
 * Configure per-product quantity thresholds for commercial customers to qualify for wholesale pricing.
 * When a commercial customer orders >= threshold kg, they automatically get wholesale tier pricing.
 */

import { useState, useEffect } from 'react'
import {
  fetchWholesaleThresholds,
  createWholesaleThreshold,
  updateWholesaleThreshold,
  deleteWholesaleThreshold,
  fetchProducts,
  fetchCustomerTypes,
  fetchPricingSettings,
  updateWholesaleUpgradeCustomerTypes,
} from '../supabase-client'
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown'
import { useDataCache } from '../contexts/DataCacheContext'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  AlertCircle,
  Check,
  Plus,
  Pencil,
  Trash2,
  Warehouse,
  Info,
  Scale
} from 'lucide-react'

export default function WholesaleThresholds() {
  const { invalidateCache } = useDataCache()
  const [thresholds, setThresholds] = useState([])
  const [products, setProducts] = useState([])
  const [customerTypes, setCustomerTypes] = useState([])
  const [upgradeCustomerTypeIds, setUpgradeCustomerTypeIds] = useState(() => new Set())
  const [savingUpgrade, setSavingUpgrade] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [saving, setSaving] = useState(false)

  // Dialog state
  const [showDialog, setShowDialog] = useState(false)
  const [editingThreshold, setEditingThreshold] = useState(null)
  const [formData, setFormData] = useState({
    product_id: '',
    min_quantity_kg: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [thresholdsData, productsData, customerTypesData, pricingSettings] = await Promise.all([
        fetchWholesaleThresholds(),
        fetchProducts(),
        fetchCustomerTypes(true),
        fetchPricingSettings(),
      ])

      setThresholds(thresholdsData)
      setProducts(productsData.filter(p => p.active))
      setCustomerTypes(customerTypesData)
      setUpgradeCustomerTypeIds(new Set(pricingSettings.wholesale_upgrade_customer_type_ids || []))
    } catch (err) {
      console.error('Failed to load wholesale thresholds:', err)
      setError('Failed to load data. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  // Products that don't have a threshold yet
  const availableProducts = products.filter(
    p => !thresholds.some(t => t.product_id === p.id)
  )

  const upgradeCustomerTypeNames = customerTypes
    .filter(ct => upgradeCustomerTypeIds.has(ct.id))
    .map(ct => ct.name)

  const handleAddNew = () => {
    setEditingThreshold(null)
    setFormData({
      product_id: availableProducts[0]?.id || '',
      min_quantity_kg: '10'
    })
    setShowDialog(true)
  }

  const handleEdit = (threshold) => {
    setEditingThreshold(threshold)
    setFormData({
      product_id: threshold.product_id,
      min_quantity_kg: threshold.min_quantity_kg.toString()
    })
    setShowDialog(true)
  }

  const handleDelete = async (threshold) => {
    if (!confirm(`Remove wholesale threshold for ${threshold.products?.strain || 'this product'}?`)) {
      return
    }

    try {
      setSaving(true)
      setError(null)
      await deleteWholesaleThreshold(threshold.id)
      await loadData()
      invalidateCache('wholesaleThresholds')
      setSuccess('Threshold removed')
      setTimeout(() => setSuccess(null), 2000)
    } catch (err) {
      console.error('Failed to delete threshold:', err)
      setError('Failed to delete threshold: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const minQty = parseFloat(formData.min_quantity_kg)
    if (isNaN(minQty) || minQty <= 0) {
      setError('Minimum quantity must be greater than 0')
      return
    }

    try {
      setSaving(true)
      setError(null)

      if (editingThreshold) {
        await updateWholesaleThreshold(editingThreshold.id, {
          min_quantity_kg: minQty
        })
        setSuccess('Threshold updated')
      } else {
        await createWholesaleThreshold({
          product_id: formData.product_id,
          min_quantity_kg: minQty
        })
        setSuccess('Threshold created')
      }

      await loadData()
      invalidateCache('wholesaleThresholds')
      setShowDialog(false)
      setTimeout(() => setSuccess(null), 2000)
    } catch (err) {
      console.error('Failed to save threshold:', err)
      setError('Failed to save: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId)
    return product?.strain || 'Unknown'
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Wholesale Thresholds</CardTitle>
          <CardDescription>Loading threshold data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-green-600" />
                Wholesale Thresholds
              </CardTitle>
              <CardDescription>
                Set per-product quantity thresholds. Customer types selected below automatically receive wholesale pricing once the threshold is met.
              </CardDescription>
            </div>
            <Button onClick={handleAddNew} disabled={availableProducts.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Add Threshold
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <Check className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {/* Customer-type configuration */}
          <div className="bg-slate-50 border rounded-lg p-4 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium">Apply wholesale upgrade to:</label>
            <MultiSelectDropdown
              options={customerTypes.map(t => ({ id: t.id, label: t.name }))}
              selected={upgradeCustomerTypeIds}
              onChange={async (next) => {
                setUpgradeCustomerTypeIds(next)
                setSavingUpgrade(true)
                try {
                  await updateWholesaleUpgradeCustomerTypes([...next])
                  setSuccess('Saved.')
                  setTimeout(() => setSuccess(null), 2000)
                } catch (err) {
                  console.error(err)
                  setError('Failed to save customer-type selection.')
                } finally {
                  setSavingUpgrade(false)
                }
              }}
              allLabel="None"
              width="w-72"
              emptyLabel="No active customer types"
            />
            {savingUpgrade && <span className="text-xs text-muted-foreground">Saving…</span>}
          </div>

          {/* Explanation */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-medium mb-1">How Wholesale Thresholds Work:</p>
                <p>
                  When a customer in one of the selected customer types above orders at least the threshold quantity
                  of a product, they automatically receive <strong>wholesale</strong> tier pricing for that product.
                </p>
                <p className="mt-2 text-green-700">
                  Example: If Lion's Mane has a 5kg threshold, a qualifying customer ordering 6kg gets wholesale pricing.
                </p>
              </div>
            </div>
          </div>

          {/* Thresholds Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Minimum Quantity</TableHead>
                  <TableHead>Effect</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {thresholds.map(threshold => (
                  <TableRow key={threshold.id}>
                    <TableCell className="font-medium">
                      {threshold.products?.strain || getProductName(threshold.product_id)}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-lg">{threshold.min_quantity_kg}</span>
                      <span className="text-muted-foreground ml-1">kg</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                        <span className="truncate max-w-[260px]" title={upgradeCustomerTypeNames.join(', ')}>
                          {upgradeCustomerTypeNames.length === 0 ? 'No customer types selected' : upgradeCustomerTypeNames.join(', ')}
                        </span>
                        <span className="text-green-600">→</span>
                        <span className="flex items-center gap-1 text-green-600 font-medium">
                          <Warehouse className="h-4 w-4" />
                          Wholesale
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(threshold)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(threshold)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {thresholds.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No wholesale thresholds configured yet.
                      <br />
                      <span className="text-sm">
                        Add thresholds to enable automatic tier upgrades for high-volume orders.
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Products without thresholds */}
          {availableProducts.length > 0 && thresholds.length > 0 && (
            <div className="text-sm text-muted-foreground">
              <strong>{availableProducts.length}</strong> products without thresholds:
              {' '}
              {availableProducts.slice(0, 5).map(p => p.strain).join(', ')}
              {availableProducts.length > 5 && ` and ${availableProducts.length - 5} more`}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingThreshold ? 'Edit Wholesale Threshold' : 'Add Wholesale Threshold'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product">Product</Label>
              {editingThreshold ? (
                <Input
                  value={getProductName(formData.product_id)}
                  disabled
                  className="bg-slate-50"
                />
              ) : (
                <Select
                  value={formData.product_id}
                  onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProducts.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.strain}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">Minimum Quantity (kg)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="quantity"
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={formData.min_quantity_kg}
                  onChange={(e) => setFormData({ ...formData, min_quantity_kg: e.target.value })}
                  className="max-w-32"
                  required
                />
                <span className="text-muted-foreground">kg</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Qualifying customers ordering this amount or more will get wholesale pricing.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingThreshold ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
